import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import CountryCode from "../../data/countrycode.json";
import { apiConnector } from "../../services/apiconnector";
import { contactusEndpoint } from "../../services/apis";
import { DS } from "../../utils/designSystem";

const inputCls =
  "w-full rounded-lg border border-richblack-700 bg-richblack-700 px-4 py-3 text-sm text-richblack-5 placeholder-richblack-400 outline-none transition-colors duration-150 focus:border-yellow-50/60 focus:ring-1 focus:ring-yellow-50/30";

const labelCls = "mb-1.5 block text-sm font-medium text-richblack-200";

const ContactUsForm = () => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitSuccessful },
  } = useForm();

  const submitContactForm = async (data) => {
    const toastId = toast.loading("Sending your message…");
    try {
      setLoading(true);
      await apiConnector("POST", contactusEndpoint.CONTACT_US_API, data);
      toast.dismiss(toastId);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ email: "", firstname: "", lastname: "", message: "", phoneNo: "" });
    }
  }, [reset, isSubmitSuccessful]);

  return (
    <form className="flex flex-col gap-5" onSubmit={handleSubmit(submitContactForm)}>
      {/* Name row */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="firstname" className={labelCls}>
            First Name <span className="text-pink-200">*</span>
          </label>
          <input
            type="text"
            id="firstname"
            placeholder="John"
            className={inputCls}
            {...register("firstname", { required: "First name is required" })}
          />
          {errors.firstname && (
            <p className="mt-1 text-xs text-pink-200">{errors.firstname.message}</p>
          )}
        </div>
        <div>
          <label htmlFor="lastname" className={labelCls}>
            Last Name
          </label>
          <input
            type="text"
            id="lastname"
            placeholder="Doe"
            className={inputCls}
            {...register("lastname")}
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className={labelCls}>
          Email Address <span className="text-pink-200">*</span>
        </label>
        <input
          type="email"
          id="email"
          placeholder="you@example.com"
          className={inputCls}
          {...register("email", {
            required: "Email is required",
            pattern: { value: /^\S+@\S+\.\S+$/, message: "Enter a valid email" },
          })}
        />
        {errors.email && (
          <p className="mt-1 text-xs text-pink-200">{errors.email.message}</p>
        )}
      </div>

      {/* Phone */}
      <div>
        <label htmlFor="phoneNo" className={labelCls}>
          Phone Number <span className="text-pink-200">*</span>
        </label>
        <div className="flex gap-3">
          <select
            className={`${inputCls} w-28 flex-shrink-0`}
            {...register("countrycode", { required: true })}
          >
            {CountryCode.map((ele, i) => (
              <option key={i} value={ele.code}>
                {ele.code}
              </option>
            ))}
          </select>
          <input
            type="tel"
            id="phoneNo"
            placeholder="98765 43210"
            className={inputCls}
            {...register("phoneNo", {
              required: "Phone number is required",
              minLength: { value: 10, message: "Minimum 10 digits" },
              maxLength: { value: 12, message: "Maximum 12 digits" },
            })}
          />
        </div>
        {errors.phoneNo && (
          <p className="mt-1 text-xs text-pink-200">{errors.phoneNo.message}</p>
        )}
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className={labelCls}>
          Message <span className="text-pink-200">*</span>
        </label>
        <textarea
          id="message"
          rows={5}
          placeholder="Tell us how we can help…"
          className={`${inputCls} resize-none`}
          {...register("message", { required: "Message is required" })}
        />
        {errors.message && (
          <p className="mt-1 text-xs text-pink-200">{errors.message.message}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        className={`${DS.btn.primary} w-full justify-center disabled:cursor-not-allowed disabled:opacity-60`}
      >
        {loading ? "Sending…" : "Send Message"}
      </button>
    </form>
  );
};

export default ContactUsForm;
